// État éphémère par écran : aucun dossier, document ou jeton dans Vuex/storage.
export function createTimelineState() {
    return { items: [], meta: null, loading: false, error: '', details: {}, detailLoading: {}, detailErrors: {},
        preview: { open: false, loading: false, error: '', data: null, consultationId: null, type: '' } }
}

export function timelineError(error) {
    const data = error && error.response && error.response.data
    if (data && data.message) return data.message
    const status = Number(error && error.response && error.response.status)
    if ([401, 403, 404].includes(status)) return 'Cet historique n’est pas disponible pour votre profil actif. Vos droits ont peut-être changé.'
    if (status === 422) return 'Les critères de recherche ne sont pas valides.'
    if (status >= 500) return 'Le service d’historique est temporairement indisponible. Réessayez.'
    return 'Impossible de charger l’historique. Vérifiez la connexion puis réessayez.'
}

const sameId = (a, b) => Number.isInteger(Number(a)) && Number(a) > 0 && Number(a) === Number(b)
function verify(data, patient, consultation) {
    if (!data || !sameId(data.patient_id, patient) || (consultation != null && !sameId(data.consultation_id, consultation))) {
        throw new Error('Contexte du dossier incohérent')
    }
}

export function createTimelineSession(api, state, context) {
    let generation = 0, listGeneration = 0, previewGeneration = 0, disposed = false
    let pendingDetails = {}
    const stamp = () => ({ generation, identity: context() })
    const current = ticket => !disposed && ticket.generation === generation && ticket.identity === context()
    function closePreview() {
        previewGeneration++
        state.preview = createTimelineState().preview
    }
    function reset() {
        generation++; listGeneration++; closePreview(); pendingDetails = {}
        api.invalidate()
        Object.assign(state, createTimelineState())
    }
    function fail(error) {
        if ([401, 403, 404].includes(Number(error && error.response && error.response.status))) {
            reset(); state.error = timelineError(error)
        }
    }
    async function load(patient, filters) {
        reset()
        const ticket = stamp(), request = ++listGeneration
        state.loading = true
        try {
            const result = await api.list(patient, filters)
            if (!current(ticket) || request !== listGeneration) return null
            verify(result && result.meta, patient)
            if (!Array.isArray(result.data) || result.data.some(item => !sameId(item.patient_id, patient) || !sameId(item.id, item.id))) throw new Error('Contexte du dossier incohérent')
            state.items = result.data; state.meta = result.meta
            return result
        } catch (error) {
            if (current(ticket)) { fail(error); state.error = timelineError(error) }
            return null
        } finally {
            if (current(ticket)) state.loading = false
        }
    }
    function details(patient, consultation, fresh = false) {
        const key = String(consultation)
        if (!fresh && state.details[key]) return Promise.resolve(state.details[key])
        if (pendingDetails[key]) return pendingDetails[key]
        const ticket = stamp()
        state.detailLoading = { ...state.detailLoading, [key]: true }
        state.detailErrors = { ...state.detailErrors, [key]: '' }
        const request = api.details(patient, consultation).then(result => {
            if (!current(ticket)) return null
            verify(result && result.data, patient, consultation)
            const data = result.data
            if (!data.summary || !sameId(data.summary.id, consultation) || !sameId(data.summary.patient_id, patient)
                || !Array.isArray(data.evolutions) || !Array.isArray(data.historiques)
                || data.historiques.some(entry => !sameId(entry.consultation_id, consultation))
                || data.evolutions.some(group => !Array.isArray(group.entries) || group.entries.some(entry => !sameId(entry.consultation_id, consultation)))) throw new Error('Contexte du dossier incohérent')
            state.details = { ...state.details, [key]: data }
            return data
        }).catch(error => {
            if (!current(ticket)) return null
            fail(error)
            if (current(ticket)) state.detailErrors = { ...state.detailErrors, [key]: timelineError(error) }
            throw error
        }).finally(() => {
            if (current(ticket)) {
                delete pendingDetails[key]
                state.detailLoading = { ...state.detailLoading, [key]: false }
            }
        })
        pendingDetails[key] = request
        return request
    }
    async function preview(patient, consultation, type) {
        const ticket = stamp(), request = ++previewGeneration
        state.preview = { open: true, loading: true, error: '', data: null, consultationId: consultation, type }
        try {
            const result = await api.artifacts(patient, consultation, type)
            if (!current(ticket) || request !== previewGeneration) return null
            verify(result && result.data, patient, consultation)
            if (result.data.type !== type || !Array.isArray(result.data.documents)) throw new Error('Contexte du document incohérent')
            state.preview.data = result.data
            return result.data
        } catch (error) {
            if (current(ticket) && request === previewGeneration) {
                fail(error)
                if (current(ticket)) state.preview.error = timelineError(error)
            }
            return null
        } finally {
            if (current(ticket) && request === previewGeneration) state.preview.loading = false
        }
    }
    return { load, details, preview, reset, closePreview,
        dispose() { reset(); disposed = true } }
}

export function timelineDate(value, timezone) {
    if (!value || isNaN(new Date(value).getTime())) return 'Date non renseignée'
    if (!timezone) return 'Fuseau de l’établissement non renseigné'
    const options = { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    if (timezone) options.timeZone = timezone
    try { return new Intl.DateTimeFormat('fr-FR', options).format(new Date(value)) }
    catch (error) { return 'Fuseau de l’établissement non valide' }
}

export function editorLocation(summary) {
    const ctx = summary && summary.editor_context
    const modifiable = summary && (summary.status === 'ongoing' ||
        (summary.status === 'closed' && summary.complement && summary.complement.possible === true))
    if (!summary || !summary.permissions || summary.permissions.can_open_editor !== true || !modifiable
        || !ctx || !sameId(ctx.patient_id, summary.patient_id) || !sameId(ctx.consultation_id, summary.id)
        || !sameId(ctx.tarif_consultation_id, ctx.tarif_consultation_id)) return null
    return { path: '/patients/' + summary.patient_id + '/consulter',
        query: { med004: String(summary.id), tarif: String(ctx.tarif_consultation_id) } }
}
